import { useSimStore } from '../../store/useSimStore';
import { FETAL_PATTERNS, FETAL_PATTERN_ORDER } from '../../engine/fetalEngine';
import HelpTip from './HelpTip';
import styles from './FetalControls.module.css';

const VARIABILITY_OPTIONS = ['absent', 'minimal', 'moderate', 'marked'];
const DECEL_OPTIONS = ['none', 'early', 'late', 'variable', 'prolonged'];

// Visible-time-window (zoom) presets, in seconds. Smaller = real-time detail;
// larger = zoomed out to see FHR/contraction trends over a longer period.
const WINDOW_OPTIONS = [150, 300, 600, 1200, 1800];
const fmtWindow = (sec) => {
  const m = sec / 60;
  return `${Number.isInteger(m) ? m : m.toFixed(1)}m`;
};

/**
 * Fetal CTG (intrapartum) controls — lives in the OB/GYN tab. A master toggle
 * shows/hides the side-by-side cardiotocograph screen; preset EFM patterns set
 * a recognisable tracing, and the sliders fine-tune baseline / variability /
 * contractions on top.
 */
export default function FetalControls() {
  const fetalMonitor = useSimStore((s) => s.fetalMonitor);
  const setFetalMonitor = useSimStore((s) => s.setFetalMonitor);
  const fetal = useSimStore((s) => s.fetal);
  const setFetal = useSimStore((s) => s.setFetal);
  const recordCaseEvent = useSimStore((s) => s.recordCaseEvent);

  const applyPreset = (key) => {
    const p = FETAL_PATTERNS[key];
    if (!p) return;
    const { name, category, description, ...fields } = p; // eslint-disable-line no-unused-vars
    setFetal({ ...fields, pattern: key });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.headTitle}>Fetal CTG Monitor<HelpTip label="About the fetal monitor">Adds a third screen with fetal heart rate and uterine contractions. Choose a CTG pattern below; it is set manually and does not change with the maternal scenario.</HelpTip></span>
          <span className={styles.headSub}>Intrapartum FHR + tocodynamometer · shows beside the vitals monitor</span>
        </div>
        <button
          className={`${styles.toggle} ${fetalMonitor ? styles.toggleOn : ''}`}
          onClick={() => {
            setFetalMonitor(!fetalMonitor);
            recordCaseEvent('screen', fetalMonitor ? 'Fetal CTG screen off' : 'Fetal CTG screen on');
          }}
          role="switch"
          aria-checked={fetalMonitor}
        >
          <span className={styles.toggleKnob} />
          <span className={styles.toggleLabel}>{fetalMonitor ? 'ON' : 'OFF'}</span>
        </button>
      </div>

      {fetalMonitor && (
        <div className={styles.body}>
          {/* Preset EFM patterns */}
          <div className={styles.presetGrid}>
            {FETAL_PATTERN_ORDER.map((key) => (
              <button
                key={key}
                className={`${styles.presetBtn} ${fetal.pattern === key ? styles.presetActive : ''}`}
                onClick={() => applyPreset(key)}
                title={FETAL_PATTERNS[key].description}
              >
                {FETAL_PATTERNS[key].name}
              </button>
            ))}
          </div>

          {/* Time window / zoom */}
          <div className={styles.control}>
            <div className={styles.controlLabel}>
              <span>Time window (zoom out for trends)</span>
              <span className={styles.controlValue}>{fmtWindow(fetal.windowSec ?? 150)}</span>
            </div>
            <div className={styles.segmented}>
              {WINDOW_OPTIONS.map((sec) => (
                <button
                  key={sec}
                  className={`${styles.segBtn} ${(fetal.windowSec ?? 150) === sec ? styles.segActive : ''}`}
                  onClick={() => setFetal({ windowSec: sec })}
                >
                  {fmtWindow(sec)}
                </button>
              ))}
            </div>
          </div>

          {/* Baseline slider */}
          <div className={styles.control}>
            <div className={styles.controlLabel}>
              <span>Baseline FHR</span>
              <span className={styles.controlValue}>{Math.round(fetal.fhrBaseline)} bpm</span>
            </div>
            <input
              type="range" min="60" max="200" step="1" value={fetal.fhrBaseline}
              onChange={(e) => setFetal({ fhrBaseline: Number(e.target.value), pattern: null })}
            />
          </div>

          {/* Variability */}
          <div className={styles.control}>
            <div className={styles.controlLabel}><span>Variability</span></div>
            <div className={styles.segmented}>
              {VARIABILITY_OPTIONS.map((v) => (
                <button
                  key={v}
                  className={`${styles.segBtn} ${fetal.variability === v && !fetal.sinusoidal ? styles.segActive : ''}`}
                  onClick={() => setFetal({ variability: v, sinusoidal: false, pattern: null })}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Decelerations */}
          <div className={styles.control}>
            <div className={styles.controlLabel}><span>Decelerations</span></div>
            <div className={styles.segmented}>
              {DECEL_OPTIONS.map((d) => (
                <button
                  key={d}
                  className={`${styles.segBtn} ${fetal.decel === d && !fetal.sinusoidal ? styles.segActive : ''}`}
                  onClick={() => setFetal({ decel: d, sinusoidal: false, pattern: null })}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Accelerations + Sinusoidal toggles */}
          <div className={styles.control}>
            <div className={styles.chipRow}>
              <button
                className={`${styles.chip} ${fetal.accels && !fetal.sinusoidal ? styles.chipOn : ''}`}
                onClick={() => setFetal({ accels: !fetal.accels, pattern: null })}
              >
                Accelerations {fetal.accels ? '✓' : ''}
              </button>
              <button
                className={`${styles.chip} ${fetal.sinusoidal ? styles.chipOn : ''}`}
                onClick={() => setFetal({ sinusoidal: !fetal.sinusoidal, pattern: fetal.sinusoidal ? null : 'sinusoidal' })}
                title="Ominous smooth sine wave — fetal anaemia / severe hypoxia"
              >
                Sinusoidal {fetal.sinusoidal ? '✓' : ''}
              </button>
            </div>
          </div>

          {/* Contraction frequency + strength */}
          <div className={styles.control}>
            <div className={styles.controlLabel}>
              <span>Contractions</span>
              <span className={styles.controlValue}>{fetal.contractionFreq}/10 min</span>
            </div>
            <input
              type="range" min="0" max="6" step="1" value={fetal.contractionFreq}
              onChange={(e) => setFetal({ contractionFreq: Number(e.target.value) })}
            />
          </div>
          <div className={styles.control}>
            <div className={styles.controlLabel}>
              <span>Contraction strength</span>
              <span className={styles.controlValue}>{fetal.contractionStrength}</span>
            </div>
            <input
              type="range" min="0" max="100" step="5" value={fetal.contractionStrength}
              onChange={(e) => setFetal({ contractionStrength: Number(e.target.value) })}
              disabled={fetal.contractionFreq === 0}
            />
          </div>
        </div>
      )}
    </div>
  );
}
